import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

export interface NavCardProps {
  /** A dónde lleva. Es un enlace de verdad: se puede abrir en otra pestaña. */
  to: string;
  title: string;
  /** Segunda línea, normalmente un identificador. */
  subtitle?: ReactNode;
  /** Cuerpo: estado, metadatos, lo que distinga a este de los demás. */
  children?: ReactNode;
  /** Slot arriba a la derecha: un badge, una acción. */
  aside?: ReactNode;
  className?: string;
}

/**
 * Tarjeta que navega.
 *
 * Existe aparte de `SelectionCard` porque hacen cosas distintas y se anuncian
 * distinto: aquélla es un control (`role="checkbox"`, `aria-checked`) y ésta es
 * un enlace. Usar la de selección para navegar haría que un lector de pantalla
 * dijera "casilla, no marcada" sobre algo que abre una página.
 *
 * Es un `<Link>` y no un `<div onClick>`: así se puede abrir en otra pestaña
 * con el clic del medio o ⌘+clic, que es como se comparan dos tenants sin
 * perder la lista.
 */
export function NavCard({ to, title, subtitle, children, aside, className }: NavCardProps) {
  return (
    <Link
      to={to}
      data-testid={`nav-card-${to.split('/').pop()}`}
      className={`group flex flex-col gap-2 rounded-lg border border-edge-subtle bg-surface-base p-4 shadow-sm transition-colors hover:border-edge-focus hover:bg-surface-sunken ${className ?? ''}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate font-medium text-content-primary">{title}</h3>
          {subtitle ? (
            <p className="truncate font-mono text-xs text-content-tertiary">{subtitle}</p>
          ) : null}
        </div>
        {aside ? <div className="shrink-0">{aside}</div> : null}
      </div>

      {children ? <div className="text-xs text-content-tertiary">{children}</div> : null}
    </Link>
  );
}
